import {
	Anchor,
	HighlightMatch,
	TableFilterBar,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import { Pagination } from "molecules/pagination/Pagination";
import { SortHeaderButton } from "molecules/sortAndFilterHeader/SortHeaderButton";
import { matchesByName } from "src/features/integrations/overview/table/matchesDataSource.ts";
import { useLocale } from "src/locales/useLocale.ts";

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
	useLoadedCompanyDomain,
	useLoadedCompanyHierarchy,
} from "src/routes/routeDataHooks.js";

import { useLastActiveContext } from "src/common/utils/hooks/useLastActiveContext.js";
import { useDocumentTitle } from "src/common/utils/hooks/useDocumentTitle";
import { stringSort } from "src/common/utils/hooks/useSort";
import { Card } from "src/features/planning/components/Card";
import { EmptyState } from "src/features/planning/components/EmptyState";
import { FilterNoResults } from "src/features/planning/components/FilterNoResults";
import { DataTable } from "molecules/data-table/DataTable";
import { useLoadedLegalEntities } from "src/routes/routeDataHooks";
import { DOMAIN_TERMS } from "src/common/domainTerms.js";
import { useGetAllIntegrationsQuery } from "src/common/service/fusionHub/fusionHubApi.js";
import { parseFusionHubSource } from "src/features/integrations/fusionHubSource.js";
import { useCanAddDataSource } from "src/features/integrations/useCanAddDataSource.js";
import { SourceSystemLogoByName } from "src/features/integrations/overview/table/SourceSystemLogo.js";
import {
	getSourceMetadata,
	useSourceSystemMetadataMap,
} from "src/features/integrations/metadata.js";

// Only a FusionHub-backed entity gets a visible data-source link — mirrors LegalEntitySource's rendering condition below.
const hasVisibleDataSource = (legalEntity: { source?: string | undefined }) =>
	parseFusionHubSource(legalEntity.source) !== undefined;

const PAGE_SIZE = 15;

const LegalEntitySource = ({ source }: { source: string }) => {
	const companyDomain = useLoadedCompanyDomain();
	const metadataMap = useSourceSystemMetadataMap();
	// Same query IntegrationsTableContent/ExopenProductsWidget already fire for this
	// domain — RTK Query dedupes into a single request, so this adds no extra fetch.
	const { data: integrations } = useGetAllIntegrationsQuery({
		companyDomainId: companyDomain.id,
	});
	const integrationId = parseFusionHubSource(source);
	if (integrationId !== undefined) {
		const sourceSystemType = integrations?.items.find(
			({ integration }) => integration.id === integrationId,
		)?.integration.type;
		if (!sourceSystemType) {
			// The integration that created this company has since been deleted
			// (or, rarer, its row is gone outright) — nothing to link to or show
			// a logo for. Render like "no source" instead of a dead link.
			return <span className="text-gray-400">–</span>;
		}
		const { name } = getSourceMetadata(metadataMap, sourceSystemType);
		return (
			<Tooltip delay={300}>
				<TooltipTrigger asChild>
					<Anchor
						component={Link}
						viewTransition
						to={`/company-domains/${companyDomain.id}/data-sources/${integrationId}`}
						aria-label={name}
					>
						<SourceSystemLogoByName
							sourceSystemName={sourceSystemType}
							size="small"
						/>
					</Anchor>
				</TooltipTrigger>
				<TooltipContent>{name}</TooltipContent>
			</Tooltip>
		);
	}
	return <span className="text-gray-400">–</span>;
};

export type LegalEntityListItem = {
	isGroup: boolean;
	id: string;
	name: string;
	currencyBase: string;
	createdAt: string;
	source?: string | undefined;
};

const useLegalEntitiesWithGroupStructure = () => {
	const legalEntities = useLoadedLegalEntities();
	const hierarchy = useLoadedCompanyHierarchy();
	const { t } = useTranslation();

	return useMemo(() => {
		let items: LegalEntityListItem[] = [];
		// WHY: the hierarchy comes back whole, so a user permitted no company would still see the group row.
		if (hierarchy.hierarchy && legalEntities.length > 0) {
			items.push({
				id: hierarchy.hierarchy.legalEntity.id,
				name: t("Group ({{name}})", {
					name: hierarchy.hierarchy.legalEntity.name,
				}),
				createdAt: hierarchy.hierarchy.legalEntity.createdAt,
				currencyBase: hierarchy.hierarchy.legalEntity.currencyBase,
				// WHY: the group row represents this same entity — its source was
				// dropped here, always showing "–" even when one was connected.
				source: hierarchy.hierarchy.legalEntity.source,
				isGroup: true,
			});
		}
		items = items.concat(
			legalEntities.map((legalEntity) => {
				return { isGroup: false, ...legalEntity };
			}),
		);
		return items;
	}, [hierarchy.hierarchy, legalEntities, t]);
};

type Sort =
	| "a-z"
	| "z-a"
	| "currency-asc"
	| "currency-desc"
	| "source-asc"
	| "source-desc";

// WHY "0"/"1" not booleans: keeps this comparator symmetric with the other string-keyed sorts (stringSort on a two-value key).
const sourceKeyOf = (item: LegalEntityListItem) =>
	hasVisibleDataSource(item) ? "0" : "1";

export const LegalEntitiesManager = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const legalEntities = useLegalEntitiesWithGroupStructure();
	const { write: writeLastActiveContext } = useLastActiveContext();
	const [draft, setDraft] = useState("");
	const [page, setPage] = useState(1);
	const [sort, setSort] = useState<Sort | null>(null);

	const { lang } = useLocale();
	const activeTerms = useMemo(
		() => [draft.trim()].filter((term) => term !== ""),
		[draft],
	);
	const currencyNames = useMemo(
		() => new Intl.DisplayNames([lang], { type: "currency" }),
		[lang],
	);
	const matchesLegalEntity = useCallback(
		(legalEntity: LegalEntityListItem, termsToCheck: string[]) => {
			const fields = [
				legalEntity.name,
				legalEntity.currencyBase,
				currencyNames.of(legalEntity.currencyBase),
				hasVisibleDataSource(legalEntity)
					? t("Has data source")
					: t("No data source"),
			];
			return termsToCheck.every((term) =>
				fields.some((field) => field && matchesByName(term, field)),
			);
		},
		[t, currencyNames],
	);
	const filteredLegalEntities = useMemo(
		() =>
			legalEntities.filter((legalEntity) =>
				matchesLegalEntity(legalEntity, activeTerms),
			),
		[legalEntities, activeTerms, matchesLegalEntity],
	);
	const sortedLegalEntities = useMemo(() => {
		switch (sort) {
			case "a-z":
			case null:
				return filteredLegalEntities.toSorted((a, b) =>
					stringSort(a.name, b.name),
				);
			case "z-a":
				return filteredLegalEntities.toSorted((a, b) =>
					stringSort(b.name, a.name),
				);
			case "currency-asc":
				return filteredLegalEntities.toSorted((a, b) =>
					stringSort(a.currencyBase, b.currencyBase),
				);
			case "currency-desc":
				return filteredLegalEntities.toSorted((a, b) =>
					stringSort(b.currencyBase, a.currencyBase),
				);
			case "source-asc":
				return filteredLegalEntities.toSorted((a, b) =>
					stringSort(sourceKeyOf(a), sourceKeyOf(b)),
				);
			case "source-desc":
				return filteredLegalEntities.toSorted((a, b) =>
					stringSort(sourceKeyOf(b), sourceKeyOf(a)),
				);
		}
	}, [filteredLegalEntities, sort]);

	const pagedItems = sortedLegalEntities.slice(
		(page - 1) * PAGE_SIZE,
		(page - 1) * PAGE_SIZE + PAGE_SIZE,
	);
	const totalPages = Math.ceil(sortedLegalEntities.length / PAGE_SIZE);

	useDocumentTitle(t(DOMAIN_TERMS.COMPANIES));
	const hasEntities = legalEntities.length > 0;
	const canAddDataSource = useCanAddDataSource(companyDomain.id);

	const directionOf = (asc: Sort, desc: Sort) =>
		(sort ?? "a-z") === asc
			? ("asc" as const)
			: sort === desc
				? ("desc" as const)
				: null;

	if (!hasEntities) {
		// WHY on permission: nexus returns [] for both no companies and none permitted, so advise only what the user can do.
		return (
			<Card>
				{canAddDataSource ? (
					<EmptyState
						header={t("No companies found")}
						subHeader={t(
							"Companies are automatically created when you add a data source. Add one to get started.",
						)}
						createButton={
							<Link
								to={`/company-domains/${companyDomain.id}/data-sources/new`}
							>
								<Button variant="primary">{t("Add data source")}</Button>
							</Link>
						}
					/>
				) : (
					<EmptyState
						header={t("No companies to show")}
						subHeader={t(
							"Your account has not been given access to any company in this domain. Contact your domain administrator.",
						)}
					/>
				)}
			</Card>
		);
	}

	return (
		<>
			<DataTable
				label={t(DOMAIN_TERMS.COMPANIES)}
				rows={pagedItems}
				// The group row's id can equal its representative entity's own id — prefix so both keys stay unique.
				getRowKey={(legalEntity) =>
					legalEntity.isGroup ? `group-${legalEntity.id}` : legalEntity.id
				}
				emptyState={
					<FilterNoResults
						onClearFilter={() => {
							setDraft("");
							setPage(1);
							setSort(null);
						}}
					/>
				}
				filterBar={
					<TableFilterBar
						value={draft}
						onChange={(value) => {
							setDraft(value);
							setPage(1);
						}}
						placeholder={t("Filter companies by name, currency or data source")}
					/>
				}
				columns={[
					{
						id: "name",
						width: "minmax(300px, 1fr)",
						headerClassName: "pl-5",
						cellClassName: "pl-5",
						render: (legalEntity) => (
							<Anchor
								to={{
									pathname: `/company-domains/${companyDomain.id}/legal-entities/${legalEntity.id}/info`,
									search: legalEntity.isGroup ? "?group" : "",
								}}
								component={Link}
								viewTransition
								onClick={() => {
									writeLastActiveContext({
										companyDomainId: companyDomain.id,
										legalEntityId: legalEntity.id,
									});
								}}
							>
								<HighlightMatch text={legalEntity.name} query={draft} />
							</Anchor>
						),
						header: (
							<SortHeaderButton
								label={t(DOMAIN_TERMS.COMPANY)}
								activeDirection={directionOf("a-z", "z-a")}
								onToggle={() => {
									setSort(directionOf("a-z", "z-a") === "asc" ? "z-a" : "a-z");
								}}
							/>
						),
					},
					{
						id: "currency",
						width: "10rem",
						cellClassName: "justify-center text-sm text-grey-500",
						render: (legalEntity) => (
							<HighlightMatch text={legalEntity.currencyBase} query={draft} />
						),
						header: (
							<SortHeaderButton
								label={t(DOMAIN_TERMS.CURRENCY)}
								activeDirection={directionOf("currency-asc", "currency-desc")}
								onToggle={() => {
									setSort(
										directionOf("currency-asc", "currency-desc") === "asc"
											? "currency-desc"
											: "currency-asc",
									);
								}}
							/>
						),
					},
					{
						id: "source",
						width: "12rem",
						cellClassName: "justify-center text-sm text-grey-500",
						render: (legalEntity) =>
							legalEntity.source ? (
								<LegalEntitySource source={legalEntity.source} />
							) : (
								<span className="text-gray-400">–</span>
							),
						header: (
							<SortHeaderButton
								label={t(DOMAIN_TERMS.DATA_SOURCE)}
								activeDirection={directionOf("source-asc", "source-desc")}
								onToggle={() => {
									setSort(
										directionOf("source-asc", "source-desc") === "asc"
											? "source-desc"
											: "source-asc",
									);
								}}
							/>
						),
					},
				]}
			/>
			<div className="mt-4 flex items-center justify-between gap-4">
				<span className="text-grey-500 text-sm">
					{t("{{count}} company", {
						count: sortedLegalEntities.filter(({ isGroup }) => !isGroup).length,
					})}
				</span>
				{totalPages > 1 && (
					<Pagination page={page} totalPages={totalPages} onChange={setPage} />
				)}
			</div>
		</>
	);
};

export const LegalEntitiesPage = () => <LegalEntitiesManager />;
